import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { organizationUsageTextV822 } from '../CustomerVertexLogV811/organizationUsageV822';
import {
  Button,
  Card,
  Grid,
  Icon,
  Label,
  Loader,
  Message,
  Modal,
  Segment,
  Table,
} from 'semantic-ui-react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { API, isAdmin, isRoot, showError, showSuccess } from '../../helpers';
import './Dashboard.css';
import OverviewChartsV101 from './UserOverviewV101';
import DashboardScopeV112, {
  dashboardScopeActiveV112,
  dashboardStatisticsURLV112,
} from './DashboardScopeV112';

const DASHBOARD_PERIOD_KEY = 'alfredapi.dashboard.statistics_period.v72';
const VALID_PERIODS = ['day', 'month', 'year'];

const numberFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 0,
});

const formatNumber = (value, maximumFractionDigits = 0) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits }).format(
    Number(value) || 0
  );

const formatPercent = (value, digits = 1) => formatNumber(value, digits) + '%';

const formatBytes = (value) => {
  const bytes = Number(value) || 0;
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  const amount = bytes / Math.pow(1024, unitIndex);
  return formatNumber(amount, amount >= 100 ? 0 : 1) + ' ' + units[unitIndex];
};

const formatDuration = (seconds) => {
  let remaining = Math.max(0, Math.floor(Number(seconds) || 0));
  const days = Math.floor(remaining / 86400);
  remaining %= 86400;
  const hours = Math.floor(remaining / 3600);
  remaining %= 3600;
  const minutes = Math.floor(remaining / 60);
  if (days > 0) return days + 'd ' + hours + 'h';
  if (hours > 0) return hours + 'h ' + minutes + 'm';
  return minutes + 'm';
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '—';
  return new Date(Number(timestamp) * 1000).toLocaleString();
};

const formatLatency = (value) => {
  const milliseconds = Number(value) || 0;
  if (milliseconds >= 1000) {
    return formatNumber(milliseconds / 1000, 2) + ' s';
  }
  return formatNumber(milliseconds, 0) + ' ms';
};

const wilsonScore = (success, total) => {
  if (!total) return 0;
  const z = 1.96;
  const probability = success / total;
  const zSquared = z * z;
  const numerator =
    probability +
    zSquared / (2 * total) -
    z *
      Math.sqrt(
        (probability * (1 - probability) + zSquared / (4 * total)) / total
      );
  return (numerator / (1 + zSquared / total)) * 100;
};

const pearsonCorrelation = (points) => {
  const samples = points.filter(
    (point) =>
      point.totalRequests > 0 &&
      point.elapsedTimeCount > 0 &&
      Number.isFinite(point.averageElapsedTime)
  );
  if (samples.length < 3) return null;
  const latencyMean =
    samples.reduce((sum, point) => sum + point.averageElapsedTime, 0) /
    samples.length;
  const failureMean =
    samples.reduce((sum, point) => sum + point.failureRate, 0) / samples.length;
  let numerator = 0;
  let latencyVariance = 0;
  let failureVariance = 0;
  samples.forEach((point) => {
    const latencyDelta = point.averageElapsedTime - latencyMean;
    const failureDelta = point.failureRate - failureMean;
    numerator += latencyDelta * failureDelta;
    latencyVariance += latencyDelta * latencyDelta;
    failureVariance += failureDelta * failureDelta;
  });
  const denominator = Math.sqrt(latencyVariance * failureVariance);
  if (!denominator) return null;
  return numerator / denominator;
};

const buildAdminOverview = (statistics) => {
  const models = Array.isArray(statistics?.models) ? statistics.models : [];
  const totals = models.reduce(
    (result, model) => {
      result.successCount += Number(model.success_count) || 0;
      result.failureCount += Number(model.failure_count) || 0;
      result.totalRequests += Number(model.total_requests) || 0;
      result.totalTokens += Number(model.total_tokens) || 0;
      result.quota += Number(model.quota) || 0;
      result.elapsedTimeCount += Number(model.elapsed_time_count) || 0;
      result.elapsedTimeSum +=
        (Number(model.average_elapsed_time) || 0) *
        (Number(model.elapsed_time_count) || 0);
      result.firstResponseTimeCount +=
        Number(model.first_response_time_count) || 0;
      result.firstResponseTimeSum +=
        (Number(model.average_first_response_time) || 0) *
        (Number(model.first_response_time_count) || 0);
      result.streamCount += Number(model.stream_count) || 0;
      return result;
    },
    {
      successCount: 0,
      failureCount: 0,
      totalRequests: 0,
      totalTokens: 0,
      quota: 0,
      elapsedTimeCount: 0,
      elapsedTimeSum: 0,
      firstResponseTimeCount: 0,
      firstResponseTimeSum: 0,
      streamCount: 0,
    }
  );

  totals.successRate = totals.totalRequests
    ? (totals.successCount * 100) / totals.totalRequests
    : 0;
  totals.failureRate = totals.totalRequests
    ? (totals.failureCount * 100) / totals.totalRequests
    : 0;
  totals.stabilityScore = wilsonScore(
    totals.successCount,
    totals.totalRequests
  );
  totals.averageElapsedTime = totals.elapsedTimeCount
    ? totals.elapsedTimeSum / totals.elapsedTimeCount
    : 0;
  totals.averageFirstResponseTime = totals.firstResponseTimeCount
    ? totals.firstResponseTimeSum / totals.firstResponseTimeCount
    : 0;
  totals.tokensPerRequest = totals.totalRequests
    ? totals.totalTokens / totals.totalRequests
    : 0;

  const buckets = new Map();
  models.forEach((model) => {
    const trend = Array.isArray(model.trend) ? model.trend : [];
    trend.forEach((point) => {
      const bucketStart = Number(point.bucket_start) || 0;
      if (!bucketStart) return;
      const bucket = buckets.get(bucketStart) || {
        bucketStart,
        successCount: 0,
        failureCount: 0,
        totalRequests: 0,
        totalTokens: 0,
        elapsedTimeCount: 0,
        elapsedTimeSum: 0,
        firstResponseTimeCount: 0,
        firstResponseTimeSum: 0,
      };
      const elapsedCount = Number(point.elapsed_time_count) || 0;
      const firstResponseCount = Number(point.first_response_time_count) || 0;
      bucket.successCount += Number(point.success_count) || 0;
      bucket.failureCount += Number(point.failure_count) || 0;
      bucket.totalRequests += Number(point.total_requests) || 0;
      bucket.totalTokens += Number(point.total_tokens) || 0;
      bucket.elapsedTimeCount += elapsedCount;
      bucket.elapsedTimeSum +=
        (Number(point.average_elapsed_time) || 0) * elapsedCount;
      bucket.firstResponseTimeCount += firstResponseCount;
      bucket.firstResponseTimeSum +=
        (Number(point.average_first_response_time) || 0) * firstResponseCount;
      buckets.set(bucketStart, bucket);
    });
  });

  const trend = Array.from(buckets.values())
    .sort((left, right) => left.bucketStart - right.bucketStart)
    .map((bucket) => ({
      ...bucket,
      successRate: bucket.totalRequests
        ? (bucket.successCount * 100) / bucket.totalRequests
        : 0,
      failureRate: bucket.totalRequests
        ? (bucket.failureCount * 100) / bucket.totalRequests
        : 0,
      stabilityScore: wilsonScore(bucket.successCount, bucket.totalRequests),
      averageElapsedTime: bucket.elapsedTimeCount
        ? bucket.elapsedTimeSum / bucket.elapsedTimeCount
        : 0,
      averageFirstResponseTime: bucket.firstResponseTimeCount
        ? bucket.firstResponseTimeSum / bucket.firstResponseTimeCount
        : 0,
    }));

  const riskModels = models
    .filter((model) => Number(model.total_requests) > 0)
    .sort((left, right) => {
      if (Number(left.failure_count) !== Number(right.failure_count)) {
        return Number(right.failure_count) - Number(left.failure_count);
      }
      if (Number(left.stability_score) !== Number(right.stability_score)) {
        return Number(left.stability_score) - Number(right.stability_score);
      }
      return Number(right.total_requests) - Number(left.total_requests);
    })
    .slice(0, 6);

  return {
    totals,
    trend,
    riskModels,
    latencyFailureCorrelation: pearsonCorrelation(trend),
  };
};

export const buildUserOverview = (data, asOf) => {
  const source = Array.isArray(data) ? data : [];
  const hourSeconds = 60 * 60;
  const now =
    Number.isFinite(asOf) && asOf > 0 ? asOf : Math.floor(Date.now() / 1000);
  const windowStart = now - 24 * hourSeconds;
  const firstBucket = Math.floor(windowStart / hourSeconds) * hourSeconds;
  const lastBucket = Math.floor(now / hourSeconds) * hourSeconds;
  const models = [
    ...new Set(source.map((item) => item.ModelName).filter(Boolean)),
  ];
  const hourly = {};

  for (let bucket = firstBucket; bucket <= lastBucket; bucket += hourSeconds) {
    hourly[bucket] = { hour: bucket, requests: 0, quota: 0, tokens: 0 };
    models.forEach((model) => {
      hourly[bucket][model] = 0;
    });
  }

  source.forEach((item) => {
    const bucket = Number(item.Hour);
    if (!hourly[bucket]) return;
    const tokens =
      (Number(item.PromptTokens) || 0) + (Number(item.CompletionTokens) || 0);
    hourly[bucket].requests += Number(item.RequestCount) || 0;
    hourly[bucket].quota += (Number(item.Quota) || 0) / 1000000;
    hourly[bucket].tokens += tokens;
    hourly[bucket][item.ModelName] =
      (hourly[bucket][item.ModelName] || 0) + tokens;
  });

  const trend = Object.values(hourly).sort(
    (left, right) => left.hour - right.hour
  );
  const totals = trend.reduce(
    (summary, item) => ({
      requests: summary.requests + item.requests,
      quota: summary.quota + item.quota,
      tokens: summary.tokens + item.tokens,
    }),
    { requests: 0, quota: 0, tokens: 0 }
  );
  return {
    trend,
    models,
    totals,
    activeModels: models.length,
  };
};

const DashboardCard = ({ title, meta, children, className = '' }) => (
  <Card
    fluid
    className={['dashboard-card-v72', className].filter(Boolean).join(' ')}
  >
    <Card.Content>
      <div className='dashboard-card-header-v72'>
        <div>
          <div className='dashboard-card-title-v72'>{title}</div>
          {meta ? <div className='dashboard-card-meta-v72'>{meta}</div> : null}
        </div>
      </div>
      {children}
    </Card.Content>
  </Card>
);

const KpiCard = ({ icon, label, value, hint, tone = 'blue' }) => (
  <div className={'dashboard-kpi-v72 dashboard-kpi-' + tone + '-v72'}>
    <div className='dashboard-kpi-icon-v72'>
      <Icon name={icon} />
    </div>
    <div>
      <div className='dashboard-kpi-label-v72'>{label}</div>
      <div className='dashboard-kpi-value-v72'>{value}</div>
      {hint ? <div className='dashboard-kpi-hint-v72'>{hint}</div> : null}
    </div>
  </div>
);

const MemoryBar = ({ label, value, total, color }) => {
  const safeTotal = Math.max(Number(total) || 0, 0);
  const safeValue = Math.max(Number(value) || 0, 0);
  const percent = safeTotal ? Math.min(100, (safeValue * 100) / safeTotal) : 0;
  return (
    <div className='dashboard-memory-row-v72'>
      <div className='dashboard-memory-label-v72'>
        <span>{label}</span>
        <span>
          {formatBytes(safeValue)}
          {safeTotal ? ' / ' + formatBytes(safeTotal) : ''}
        </span>
      </div>
      <div className='dashboard-memory-track-v72'>
        <span style={{ width: percent + '%', backgroundColor: color }} />
      </div>
    </div>
  );
};

const RuntimeMetric = ({ label, value, hint, status }) => (
  <div className='dashboard-runtime-metric-v72'>
    <div className='dashboard-runtime-metric-label-v72'>{label}</div>
    <div className='dashboard-runtime-metric-value-v72'>
      {value}
      {status ? (
        <span
          className={'dashboard-status-dot-v72 status-' + status + '-v72'}
        />
      ) : null}
    </div>
    {hint ? (
      <div className='dashboard-runtime-metric-hint-v72'>{hint}</div>
    ) : null}
  </div>
);

const correlationKey = (value) => {
  if (value === null) return 'insufficient';
  if (value >= 0.45) return 'strong_positive';
  if (value >= 0.15) return 'weak_positive';
  if (value <= -0.45) return 'strong_negative';
  if (value <= -0.15) return 'weak_negative';
  return 'neutral';
};

const metricStatus = (value, warning, danger) => {
  if (!Number.isFinite(value)) return 'neutral';
  if (value >= danger) return 'danger';
  if (value >= warning) return 'warning';
  return 'good';
};

const AdminDashboard = ({
  statistics,
  runtimeSnapshot,
  period,
  loading,
  statisticsFailed,
  runtimeFailed,
  refreshing,
  resettingErrors,
  onPeriodChange,
  onRefresh,
  onOpenErrorReset,
  root,
  filter,
  onFilterChange,
  t,
}) => {
  const overview = useMemo(() => buildAdminOverview(statistics), [statistics]);
  const { totals, trend, riskModels, latencyFailureCorrelation } = overview;
  const host = runtimeSnapshot?.host || {};
  const goRuntime = runtimeSnapshot?.go || {};
  const process = runtimeSnapshot?.process || {};
  const database = runtimeSnapshot?.database || {};
  const primaryPool = database.primary || {};
  const logPool = database.logs || {};
  const relation = correlationKey(latencyFailureCorrelation);
  const allPeriodOptions = [
    { key: 'day', label: t('dashboard.admin.period.day') },
    { key: 'month', label: t('dashboard.admin.period.month') },
    { key: 'year', label: t('dashboard.admin.period.year') },
  ];
  const periodOptions = root ? allPeriodOptions : allPeriodOptions.slice(0, 1);
  const errorCodes = Array.isArray(statistics?.error_codes)
    ? statistics.error_codes
    : [];
  const chartDate = (timestamp) => {
    const date = new Date(Number(timestamp) * 1000);
    if (statistics?.bucket_unit === 'hour') {
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    if (statistics?.bucket_unit === 'month') {
      return date.toLocaleDateString([], { year: '2-digit', month: 'short' });
    }
    return date.toLocaleDateString([], { month: 'numeric', day: 'numeric' });
  };

  const scoped = root && dashboardScopeActiveV112(filter);

  return (
    <>
      <div className='dashboard-toolbar-v72'>
        <div>
          <h1>{t('dashboard.admin.title')}</h1>
          <p>
            {t(
              scoped
                ? 'dashboard_scope_v112.description'
                : 'dashboard.admin.description'
            )}
          </p>
        </div>
        <div className='dashboard-toolbar-actions-v72'>
          <Button.Group size='small'>
            {periodOptions.map((option) => (
              <Button
                key={option.key}
                active={period === option.key}
                onClick={() => onPeriodChange(option.key)}
              >
                {option.label}
              </Button>
            ))}
          </Button.Group>
          <Button
            basic
            size='small'
            loading={refreshing}
            disabled={refreshing}
            onClick={onRefresh}
          >
            <Icon name='refresh' />
            {t('dashboard.admin.refresh')}
          </Button>
          {root && !scoped ? (
            <Button
              basic
              color='red'
              size='small'
              loading={resettingErrors}
              disabled={refreshing || resettingErrors}
              onClick={onOpenErrorReset}
            >
              <Icon name='eraser' />
              {t('dashboard.admin.error_reset.button')}
            </Button>
          ) : null}
        </div>
      </div>

      {root ? (
        <DashboardScopeV112 filter={filter} onChange={onFilterChange} t={t} />
      ) : null}
      {scoped ? (
        <p className='dashboard-basis-v112'>
          {t('dashboard_scope_v112.retained_logs')}
        </p>
      ) : null}

      <div className='dashboard-snapshot-note-v72'>
        <Icon name='clock outline' />
        {t('dashboard.admin.snapshot_at', {
          time: formatTimestamp(
            Math.max(
              Number(statistics?.generated_at) || 0,
              Number(runtimeSnapshot?.generated_at) || 0
            )
          ),
        })}
        <span>{t('dashboard.admin.on_demand_note')}</span>
      </div>

      {statisticsFailed ? (
        <Message
          negative
          header={t('dashboard.admin.statistics_failed')}
          content={t('dashboard.admin.retry_hint')}
        />
      ) : null}

      {loading && !statistics ? (
        <Segment basic className='dashboard-loading-v72' role='status'>
          <Loader
            active
            inline='centered'
            content={t('dashboard.admin.loading')}
          />
        </Segment>
      ) : null}
      {statistics && !statisticsFailed ? (
        <>
          <div className='dashboard-kpi-grid-v72'>
            <KpiCard
              icon='exchange'
              label={t('dashboard.admin.kpi.requests')}
              value={numberFormatter.format(totals.totalRequests)}
              hint={t('dashboard.admin.kpi.requests_hint', {
                models: statistics?.models?.length || 0,
              })}
              tone='blue'
            />
            <KpiCard
              icon='check circle'
              label={t('dashboard.admin.kpi.success_rate')}
              value={formatPercent(totals.successRate, 2)}
              hint={t('dashboard.admin.kpi.stability_hint', {
                score: formatNumber(totals.stabilityScore, 1),
              })}
              tone='green'
            />
            <KpiCard
              icon='warning sign'
              label={t('dashboard.admin.kpi.failures')}
              value={numberFormatter.format(totals.failureCount)}
              hint={t('dashboard.admin.kpi.failure_rate_hint', {
                rate: formatPercent(totals.failureRate, 2),
              })}
              tone={totals.failureCount > 0 ? 'red' : 'green'}
            />
            <KpiCard
              icon='time'
              label={t('dashboard.admin.kpi.latency')}
              value={formatLatency(totals.averageElapsedTime)}
              hint={t('dashboard.admin.kpi.success_samples', {
                count: numberFormatter.format(totals.elapsedTimeCount),
              })}
              tone='purple'
            />
            <KpiCard
              icon='lightning'
              label={t('dashboard.admin.kpi.ttft')}
              value={formatLatency(totals.averageFirstResponseTime)}
              hint={t('dashboard.admin.kpi.ttft_hint')}
              tone='cyan'
            />
            <KpiCard
              icon='code'
              label={t('dashboard.admin.kpi.token_density')}
              value={formatNumber(totals.tokensPerRequest, 0)}
              hint={t('dashboard.admin.kpi.total_tokens_hint', {
                count: numberFormatter.format(totals.totalTokens),
              })}
              tone='orange'
            />
          </div>

          <Grid stackable className='dashboard-main-grid-v72'>
            <Grid.Column width={11}>
              <DashboardCard
                title={t('dashboard.admin.relationship_chart.title')}
                meta={t('dashboard.admin.relationship_chart.meta')}
              >
                {trend.length === 0 ? (
                  <Message info content={t('dashboard.admin.no_statistics')} />
                ) : (
                  <div className='dashboard-large-chart-v72'>
                    <ResponsiveContainer width='100%' height={340}>
                      <ComposedChart
                        data={trend}
                        margin={{ top: 16, right: 8, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray='3 3'
                          vertical={false}
                          stroke='#e2e8f0'
                        />
                        <XAxis
                          dataKey='bucketStart'
                          tickFormatter={chartDate}
                          tick={{ fill: '#64748b', fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                          minTickGap={24}
                        />
                        <YAxis
                          yAxisId='requests'
                          tick={{ fill: '#64748b', fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                          allowDecimals={false}
                        />
                        <YAxis
                          yAxisId='rate'
                          orientation='right'
                          domain={[0, 100]}
                          tickFormatter={(value) => value + '%'}
                          tick={{ fill: '#64748b', fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis yAxisId='latency' hide />
                        <Tooltip
                          labelFormatter={(value) => chartDate(value)}
                          formatter={(value, name) => {
                            if (
                              name === t('dashboard.admin.chart.success_rate')
                            )
                              return [formatPercent(value, 2), name];
                            if (name === t('dashboard.admin.chart.latency'))
                              return [formatLatency(value), name];
                            return [numberFormatter.format(value), name];
                          }}
                        />
                        <Legend />
                        <Bar
                          yAxisId='requests'
                          dataKey='totalRequests'
                          name={t('dashboard.admin.chart.requests')}
                          fill='#bfdbfe'
                          radius={[5, 5, 0, 0]}
                          maxBarSize={28}
                        />
                        <Line
                          yAxisId='rate'
                          type='monotone'
                          dataKey='successRate'
                          name={t('dashboard.admin.chart.success_rate')}
                          stroke='#059669'
                          strokeWidth={3}
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                        <Line
                          yAxisId='latency'
                          type='monotone'
                          dataKey='averageElapsedTime'
                          name={t('dashboard.admin.chart.latency')}
                          stroke='#7c3aed'
                          strokeWidth={2}
                          strokeDasharray='6 4'
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </DashboardCard>
            </Grid.Column>
            <Grid.Column width={5}>
              <DashboardCard
                title={t('dashboard.admin.relationship.title')}
                meta={t('dashboard.admin.relationship.meta')}
                className='dashboard-relation-card-v72'
              >
                <div className='dashboard-correlation-v72'>
                  <span>{t('dashboard.admin.relationship.correlation')}</span>
                  <strong>
                    {latencyFailureCorrelation === null
                      ? '—'
                      : formatNumber(latencyFailureCorrelation, 2)}
                  </strong>
                </div>
                <Label
                  color={
                    relation === 'strong_positive'
                      ? 'red'
                      : relation === 'weak_positive'
                      ? 'orange'
                      : relation === 'strong_negative'
                      ? 'green'
                      : 'grey'
                  }
                >
                  {t('dashboard.admin.relationship.' + relation)}
                </Label>
                <p className='dashboard-relation-copy-v72'>
                  {t('dashboard.admin.relationship.explanation')}
                </p>
                <div className='dashboard-relation-signals-v72'>
                  <div>
                    <span>
                      {t('dashboard.admin.relationship.failure_rate')}
                    </span>
                    <strong>{formatPercent(totals.failureRate, 2)}</strong>
                  </div>
                  <div>
                    <span>{t('dashboard.admin.relationship.latency')}</span>
                    <strong>{formatLatency(totals.averageElapsedTime)}</strong>
                  </div>
                  <div>
                    <span>{t('dashboard.admin.relationship.stability')}</span>
                    <strong>{formatNumber(totals.stabilityScore, 1)}</strong>
                  </div>
                </div>
              </DashboardCard>
            </Grid.Column>
          </Grid>
        </>
      ) : null}

      <div className='dashboard-section-heading-v72'>
        <div>
          <h2>
            {t('dashboard.admin.server.title')}
            {scoped ? (
              <span className='dashboard-runtime-scope-v112'>
                {t('dashboard_scope_v112.system_resources')}
              </span>
            ) : null}
          </h2>
          <p>{t('dashboard.admin.server.description')}</p>
        </div>
      </div>

      {runtimeFailed ? (
        <Message
          warning
          header={t('dashboard.admin.runtime_failed')}
          content={t('dashboard.admin.retry_hint')}
        />
      ) : null}

      {runtimeSnapshot ? (
        <>
          <div className='dashboard-runtime-grid-v72'>
            <RuntimeMetric
              label={t('dashboard.admin.server.host_memory')}
              value={
                host.memory_total_bytes
                  ? formatPercent(host.memory_usage_percent, 1)
                  : '—'
              }
              hint={
                host.memory_total_bytes
                  ? formatBytes(host.memory_used_bytes) +
                    ' / ' +
                    formatBytes(host.memory_total_bytes)
                  : t('dashboard.admin.server.host_unavailable')
              }
              status={metricStatus(Number(host.memory_usage_percent), 70, 85)}
            />
            <RuntimeMetric
              label={t('dashboard.admin.server.process_rss')}
              value={process.rss_bytes ? formatBytes(process.rss_bytes) : '—'}
              hint={t('dashboard.admin.server.process_rss_hint')}
            />
            <RuntimeMetric
              label={t('dashboard.admin.server.go_heap')}
              value={formatBytes(goRuntime.heap_alloc_bytes)}
              hint={t('dashboard.admin.server.go_heap_hint', {
                reserved: formatBytes(goRuntime.heap_sys_bytes),
              })}
            />
            <RuntimeMetric
              label={t('dashboard.admin.server.load_per_core')}
              value={host.available ? formatNumber(host.load_per_core, 3) : '—'}
              hint={
                host.available
                  ? t('dashboard.admin.server.load_hint', {
                      load1: formatNumber(host.load_1, 2),
                      cores: goRuntime.cpu_cores || 0,
                    })
                  : t('dashboard.admin.server.host_unavailable')
              }
              status={metricStatus(Number(host.load_per_core), 0.7, 1)}
            />
            <RuntimeMetric
              label={t('dashboard.admin.server.goroutines')}
              value={numberFormatter.format(goRuntime.goroutines || 0)}
              hint={'GOMAXPROCS ' + (goRuntime.gomaxprocs || 0)}
            />
            <RuntimeMetric
              label={t('dashboard.admin.server.gc_pause')}
              value={formatNumber(goRuntime.last_gc_pause_ms, 3) + ' ms'}
              hint={t('dashboard.admin.server.gc_hint', {
                cycles: numberFormatter.format(goRuntime.gc_cycles || 0),
              })}
              status={metricStatus(Number(goRuntime.last_gc_pause_ms), 10, 50)}
            />
            <RuntimeMetric
              label={t('dashboard.admin.server.process_uptime')}
              value={formatDuration(process.uptime_seconds)}
              hint={
                (goRuntime.version || '') +
                ' ' +
                (goRuntime.goos || '') +
                '/' +
                (goRuntime.goarch || '')
              }
            />
            <RuntimeMetric
              label={t('dashboard.admin.server.db_connections')}
              value={
                (primaryPool.in_use || 0) +
                ' / ' +
                (primaryPool.open_connections || 0)
              }
              hint={t('dashboard.admin.server.db_hint', {
                wait: numberFormatter.format(primaryPool.wait_count || 0),
              })}
            />
          </div>

          <Grid stackable className='dashboard-detail-grid-v72'>
            <Grid.Column width={9}>
              <DashboardCard
                title={t('dashboard.admin.memory.title')}
                meta={t('dashboard.admin.memory.meta')}
              >
                <div className='dashboard-memory-bars-v72'>
                  <MemoryBar
                    label={t('dashboard.admin.memory.host_used')}
                    value={host.memory_used_bytes}
                    total={host.memory_total_bytes}
                    color='#2563eb'
                  />
                  <MemoryBar
                    label={t('dashboard.admin.memory.process_rss')}
                    value={process.rss_bytes}
                    total={
                      host.memory_total_bytes ||
                      process.virtual_memory_bytes ||
                      1
                    }
                    color='#7c3aed'
                  />
                  <MemoryBar
                    label={t('dashboard.admin.memory.go_heap')}
                    value={goRuntime.heap_alloc_bytes}
                    total={process.rss_bytes || goRuntime.heap_sys_bytes || 1}
                    color='#0891b2'
                  />
                  <MemoryBar
                    label={t('dashboard.admin.memory.go_stack')}
                    value={goRuntime.stack_inuse_bytes}
                    total={process.rss_bytes || goRuntime.sys_bytes || 1}
                    color='#f59e0b'
                  />
                </div>
                <div className='dashboard-memory-footnote-v72'>
                  <Icon name='info circle' />
                  {t('dashboard.admin.memory.footnote')}
                </div>
              </DashboardCard>
            </Grid.Column>
            <Grid.Column width={7}>
              <DashboardCard
                title={t('dashboard.admin.database.title')}
                meta={
                  database.shared
                    ? t('dashboard.admin.database.shared')
                    : t('dashboard.admin.database.separate')
                }
              >
                <div className='dashboard-pool-grid-v72'>
                  <div>
                    <span>{t('dashboard.admin.database.primary')}</span>
                    <strong>
                      {primaryPool.in_use || 0} /{' '}
                      {primaryPool.max_open_connections || 0}
                    </strong>
                    <small>
                      {t('dashboard.admin.database.pool_detail', {
                        open: primaryPool.open_connections || 0,
                        idle: primaryPool.idle || 0,
                        wait: primaryPool.wait_count || 0,
                      })}
                    </small>
                  </div>
                  <div>
                    <span>{t('dashboard.admin.database.logs')}</span>
                    <strong>
                      {logPool.in_use || 0} /{' '}
                      {logPool.max_open_connections || 0}
                    </strong>
                    <small>
                      {t('dashboard.admin.database.pool_detail', {
                        open: logPool.open_connections || 0,
                        idle: logPool.idle || 0,
                        wait: logPool.wait_count || 0,
                      })}
                    </small>
                  </div>
                </div>
              </DashboardCard>
            </Grid.Column>
          </Grid>
        </>
      ) : null}

      {statistics && !statisticsFailed ? (
        <>
          <DashboardCard
            title={t('dashboard.admin.error_rank.title')}
            meta={t('dashboard.admin.error_rank.meta')}
          >
            {errorCodes.length === 0 ? (
              <Message
                positive
                content={t('dashboard.admin.error_rank.empty')}
              />
            ) : (
              <div className='dashboard-table-scroll-v72'>
                <Table basic='very' compact unstackable>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>
                        {t('dashboard.admin.error_rank.code')}
                      </Table.HeaderCell>
                      <Table.HeaderCell>HTTP</Table.HeaderCell>
                      <Table.HeaderCell textAlign='right'>
                        {t('dashboard.admin.error_rank.count')}
                      </Table.HeaderCell>
                      <Table.HeaderCell textAlign='right'>
                        {t('dashboard.admin.error_rank.share')}
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {errorCodes.slice(0, 10).map((error) => (
                      <Table.Row
                        key={error.error_code + '-' + error.http_status}
                        className='dashboard-risk-row-v72'
                      >
                        <Table.Cell>
                          <strong>{error.error_code}</strong>
                        </Table.Cell>
                        <Table.Cell>{error.http_status || '—'}</Table.Cell>
                        <Table.Cell textAlign='right'>
                          {numberFormatter.format(error.count || 0)}
                        </Table.Cell>
                        <Table.Cell textAlign='right'>
                          {formatPercent(error.share, 2)}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            )}
          </DashboardCard>

          <DashboardCard
            title={t('dashboard.admin.risk.title')}
            meta={t('dashboard.admin.risk.meta')}
          >
            {riskModels.length === 0 ? (
              <Message positive content={t('dashboard.admin.risk.empty')} />
            ) : (
              <div className='dashboard-table-scroll-v72'>
                <Table basic='very' compact unstackable>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>
                        {t('dashboard.admin.risk.model')}
                      </Table.HeaderCell>
                      <Table.HeaderCell textAlign='right'>
                        {t('dashboard.admin.risk.requests')}
                      </Table.HeaderCell>
                      <Table.HeaderCell textAlign='right'>
                        {t('dashboard.admin.risk.failures')}
                      </Table.HeaderCell>
                      <Table.HeaderCell textAlign='right'>
                        {t('dashboard.admin.risk.success_rate')}
                      </Table.HeaderCell>
                      <Table.HeaderCell textAlign='right'>
                        {t('dashboard.admin.risk.stability')}
                      </Table.HeaderCell>
                      <Table.HeaderCell textAlign='right'>
                        {t('dashboard.admin.risk.latency')}
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {riskModels.map((model) => (
                      <Table.Row
                        key={model.model_name}
                        className={
                          Number(model.failure_count) > 0
                            ? 'dashboard-risk-row-v72'
                            : ''
                        }
                      >
                        <Table.Cell>
                          <strong>{model.model_name}</strong>
                          {!model.has_sufficient_sample ? (
                            <Label size='mini' basic color='grey'>
                              {t('dashboard.admin.risk.low_sample')}
                            </Label>
                          ) : null}
                        </Table.Cell>
                        <Table.Cell textAlign='right'>
                          {numberFormatter.format(model.total_requests || 0)}
                        </Table.Cell>
                        <Table.Cell textAlign='right'>
                          <span
                            className={
                              Number(model.failure_count) > 0
                                ? 'dashboard-failure-value-v72'
                                : ''
                            }
                          >
                            {numberFormatter.format(model.failure_count || 0)}
                          </span>
                        </Table.Cell>
                        <Table.Cell textAlign='right'>
                          {formatPercent(model.success_rate, 2)}
                        </Table.Cell>
                        <Table.Cell textAlign='right'>
                          {formatNumber(model.stability_score, 1)}
                        </Table.Cell>
                        <Table.Cell textAlign='right'>
                          {formatLatency(model.average_elapsed_time)}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            )}
          </DashboardCard>
        </>
      ) : null}
    </>
  );
};

export const UserDashboard = ({
  data,
  asOf,
  loading,
  failed,
  portalProfile,
  t,
}) => {
  const overview = useMemo(() => buildUserOverview(data, asOf), [data, asOf]);

  if (loading) {
    return (
      <Segment basic className='dashboard-loading-v72'>
        <Loader
          active
          inline='centered'
          content={t('dashboard.user.loading')}
        />
      </Segment>
    );
  }
  if (failed) {
    return (
      <Message
        negative
        header={t('dashboard.user.load_failed')}
        content={t('dashboard.admin.retry_hint')}
      />
    );
  }

  return (
    <>
      <div className='dashboard-toolbar-v72'>
        <div>
          <h1>
            {portalProfile === 'vertex_only'
              ? organizationUsageTextV822(t, 'dashboard_title')
              : t('dashboard.user.title')}
          </h1>
          <p>
            {portalProfile === 'vertex_only'
              ? organizationUsageTextV822(t, 'dashboard_description')
              : t('dashboard.user.description')}
          </p>
        </div>
      </div>
      <div className='dashboard-kpi-grid-v72 dashboard-user-kpis-v72'>
        <KpiCard
          icon='exchange'
          label={t('dashboard.user.last_24h_requests')}
          value={numberFormatter.format(overview.totals.requests)}
          tone='blue'
        />
        <KpiCard
          icon='code'
          label={t('dashboard.user.last_24h_tokens')}
          value={numberFormatter.format(overview.totals.tokens)}
          tone='purple'
        />
        <KpiCard
          icon='dollar'
          label={t('dashboard.user.last_24h_quota')}
          value={formatNumber(overview.totals.quota, 4)}
          tone='orange'
        />
        <KpiCard
          icon='cube'
          label={t('dashboard.user.active_models')}
          value={numberFormatter.format(overview.activeModels)}
          tone='green'
        />
      </div>
      <OverviewChartsV101 overview={overview} t={t} />
    </>
  );
};

const Dashboard = () => {
  const { t } = useTranslation();
  const admin = isAdmin();
  const root = isRoot();
  const [period, setPeriod] = useState(() => {
    const stored = localStorage.getItem(DASHBOARD_PERIOD_KEY);
    return root && VALID_PERIODS.includes(stored) ? stored : 'day';
  });
  const [filter, setFilter] = useState({ scope: 'all', account: '', name: '' });
  const [statistics, setStatistics] = useState(null);
  const [runtimeSnapshot, setRuntimeSnapshot] = useState(null);
  const [userData, setUserData] = useState([]);
  const [userAsOf, setUserAsOf] = useState(null);
  const [portalProfile, setPortalProfile] = useState('standard');
  const [loading, setLoading] = useState(true);
  const [statisticsFailed, setStatisticsFailed] = useState(false);
  const [runtimeFailed, setRuntimeFailed] = useState(false);
  const [userFailed, setUserFailed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [errorResetOpen, setErrorResetOpen] = useState(false);
  const [resettingErrors, setResettingErrors] = useState(false);
  const statisticsRequest = useRef({ generation: 0, abort: null });
  const usageRequest = useRef({ generation: 0, abort: null });

  const loadAdminStatistics = useCallback(
    async (showLoading = true) => {
      statisticsRequest.current.abort?.abort();
      const generation = ++statisticsRequest.current.generation;
      const abort = new AbortController();
      statisticsRequest.current.abort = abort;
      if (showLoading) {
        setLoading(true);
        setStatistics(null);
      }
      setStatisticsFailed(false);
      try {
        const response = await API.get(
          dashboardStatisticsURLV112(
            period,
            root ? filter : { scope: 'all', account: '' }
          ),
          { signal: abort.signal }
        );
        if (
          generation !== statisticsRequest.current.generation ||
          abort.signal.aborted
        )
          return;
        if (!response?.data?.success || !response.data.data) {
          throw new Error(response?.data?.message || 'statistics unavailable');
        }
        setStatistics(response.data.data);
      } catch (error) {
        if (
          generation === statisticsRequest.current.generation &&
          !abort.signal.aborted
        ) {
          setStatistics(null);
          setStatisticsFailed(true);
        }
      } finally {
        if (
          showLoading &&
          generation === statisticsRequest.current.generation &&
          !abort.signal.aborted
        )
          setLoading(false);
      }
    },
    [period, root, filter]
  );

  const loadRuntime = useCallback(async () => {
    setRuntimeFailed(false);
    try {
      const response = await API.get('/api/dashboard/runtime');
      if (!response?.data?.success || !response.data.data) {
        throw new Error(response?.data?.message || 'runtime unavailable');
      }
      setRuntimeSnapshot(response.data.data);
    } catch (error) {
      setRuntimeFailed(true);
    }
  }, []);

  const loadUserDashboard = useCallback(async () => {
    usageRequest.current.abort?.abort();
    const generation = ++usageRequest.current.generation;
    const abort = new AbortController();
    usageRequest.current.abort = abort;
    setLoading(true);
    setUserFailed(false);
    try {
      const response = await API.get('/api/user/dashboard', {
        signal: abort.signal,
      });
      if (
        generation !== usageRequest.current.generation ||
        abort.signal.aborted
      )
        return;
      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'dashboard unavailable');
      }
      setUserData(response.data.data || []);
      setUserAsOf(response.data.as_of);
      setPortalProfile(response.data.portal_profile || 'standard');
    } catch (error) {
      if (
        generation === usageRequest.current.generation &&
        !abort.signal.aborted
      ) {
        setUserData([]);
        setUserFailed(true);
      }
    } finally {
      if (
        generation === usageRequest.current.generation &&
        !abort.signal.aborted
      )
        setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (admin) loadAdminStatistics(true);
    else loadUserDashboard();
    const statisticsState = statisticsRequest.current;
    const usageState = usageRequest.current;
    return () => {
      statisticsState.generation++;
      statisticsState.abort?.abort();
      usageState.generation++;
      usageState.abort?.abort();
    };
  }, [admin, loadAdminStatistics, loadUserDashboard]);

  useEffect(() => {
    if (admin) loadRuntime();
  }, [admin, loadRuntime]);

  const changePeriod = (value) => {
    if (!root) return;
    if (!VALID_PERIODS.includes(value)) return;
    if (value === period) return;
    statisticsRequest.current.generation++;
    statisticsRequest.current.abort?.abort();
    setStatistics(null);
    setPeriod(value);
    localStorage.setItem(DASHBOARD_PERIOD_KEY, value);
  };

  const changeFilter = (value) => {
    statisticsRequest.current.generation++;
    statisticsRequest.current.abort?.abort();
    setStatistics(null);
    setStatisticsFailed(false);
    setLoading(true);
    setFilter(value);
    setErrorResetOpen(false);
  };

  const refreshAdminDashboard = async () => {
    setRefreshing(true);
    await Promise.all([loadAdminStatistics(false), loadRuntime()]);
    setRefreshing(false);
  };

  const resetDashboardErrors = async () => {
    setResettingErrors(true);
    try {
      const response = await API.post('/api/dashboard/errors/reset');
      if (!response?.data?.success) {
        throw new Error(
          response?.data?.message || t('dashboard.admin.error_reset.failed')
        );
      }
      setErrorResetOpen(false);
      await loadAdminStatistics(false);
      showSuccess(t('dashboard.admin.error_reset.success'));
    } catch (error) {
      showError(
        error?.response?.data?.message ||
          error?.message ||
          t('dashboard.admin.error_reset.failed')
      );
    } finally {
      setResettingErrors(false);
    }
  };

  return (
    <div
      className={
        'dashboard-container management-container-v4 dashboard-v72' +
        (admin ? '' : ' dashboard-user-v101')
      }
    >
      {admin ? (
        <AdminDashboard
          statistics={statistics}
          runtimeSnapshot={runtimeSnapshot}
          period={period}
          loading={loading}
          statisticsFailed={statisticsFailed}
          runtimeFailed={runtimeFailed}
          refreshing={refreshing}
          resettingErrors={resettingErrors}
          onPeriodChange={changePeriod}
          onRefresh={refreshAdminDashboard}
          onOpenErrorReset={() => setErrorResetOpen(true)}
          root={root}
          filter={filter}
          onFilterChange={changeFilter}
          t={t}
        />
      ) : (
        <UserDashboard
          data={userData}
          asOf={userAsOf}
          loading={loading}
          failed={userFailed}
          portalProfile={portalProfile}
          t={t}
        />
      )}
      {root ? (
        <Modal
          open={errorResetOpen}
          onClose={() => !resettingErrors && setErrorResetOpen(false)}
          size='tiny'
        >
          <Modal.Header>{t('dashboard.admin.error_reset.title')}</Modal.Header>
          <Modal.Content>
            <p>{t('dashboard.admin.error_reset.description')}</p>
            <Message
              warning
              content={t('dashboard.admin.error_reset.warning')}
            />
          </Modal.Content>
          <Modal.Actions>
            <Button
              disabled={resettingErrors}
              onClick={() => setErrorResetOpen(false)}
            >
              {t('dashboard.admin.error_reset.cancel')}
            </Button>
            <Button
              negative
              loading={resettingErrors}
              disabled={resettingErrors}
              onClick={resetDashboardErrors}
            >
              <Icon name='eraser' />
              {t('dashboard.admin.error_reset.confirm')}
            </Button>
          </Modal.Actions>
        </Modal>
      ) : null}
    </div>
  );
};

export default Dashboard;
